import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useSetAtom } from "jotai";
import { IconButton, InputAdornment, Box, Typography, TextField, Button, Card, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { setAuthAtom } from "../../state/atoms/authAtoms";
import { AccountType, Role, UserType } from "../../types/enums";
import authService from "../../services/auth/authService";
import { EnvironmentBadge } from "../../components/EnvironmentBadge";

const Login: React.FC = () => {
  const navigate = useNavigate();
  const setAuth = useSetAtom(setAuthAtom);
  const [identifier, setIdentifier] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openForgotPassword, setOpenForgotPassword] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState<string | null>(null);



  const routeMapping: { [key: string]: string } = {
    [`${null}-${UserType.PERSONAL}-${null}`]: "/vcat-user/shooting-data",
    [`${Role.ADMIN}-${UserType.MANAGED}-${AccountType.CLIENT}`]: "/client-admin/billing",
    [`${Role.USER}-${UserType.MANAGED}-${AccountType.CLIENT}`]: "/client-user",
    [`${Role.SUPER_USER}-${UserType.MANAGED}-${AccountType.EYEVIATION}`]: "/eyeviation-super/account-roots",
    [`${Role.SUPER_USER}-${UserType.MANAGED}-${AccountType.CLIENT}`]: "/eyeviation-super/account-roots",
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { user, token } = await authService.login(identifier, password);

      setAuth({ user, token });

      const role = user.role ? user.role.toUpperCase() : "null";
      const userType = user.user_type?.toUpperCase();
      const accountType = user.account_type ? user.account_type.toUpperCase() : "null";

      const routeKey = `${role}-${userType}-${accountType}`;
      const redirectPath = routeMapping[routeKey] || "/unauthorized";

      navigate(redirectPath);
    } catch (err) {
      setError((err as Error).message || "Invalid username or password");
    } finally {
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };
  const handleForgotPassword = async () => {
    setForgotPasswordMessage(null);
    setError(null);

    if (!email) {
      setError("Please enter your email.");
      return;
    }

    try {
      await authService.forgotPassword(email);
      setForgotPasswordMessage("Check your email for reset instructions.");

      setTimeout(() => {
        setOpenForgotPassword(false);
        setEmail("");
        setForgotPasswordMessage(null);
      }, 2000);
    } catch (err) {
      setError("Failed to send reset email.");
    }
  };
  const handleCloseForgotPassword = () => {
    setOpenForgotPassword(false);
    setEmail("");
    setError(null);
    setForgotPasswordMessage(null);
  };

  return (
    <Box
      className="auth-page"
      sx={{
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: "#0E1720",
      }}
    >
      <Card elevation={0}
        sx={{
          width: 400,
          padding: 4,
          backgroundColor: "#0E1720"
        }}
      >
        <Box sx={{ textAlign: "center", marginBottom: 3 }}>
          <img
            id="vcat-logo"
            src="/logo.png"
            alt="VCAT Logo"
                        style={{
              display: "block",
              margin: "0 auto 0 auto",
              width: "150px",
            }}
          />
                  <EnvironmentBadge />

          <Typography variant="h5" sx={{ color: "#FFFFFF", fontWeight: "bold" }} id="login-heading">
            Log in
          </Typography>

        </Box>
        <Dialog
          className="auth-page"
          open={openForgotPassword}
          onClose={handleCloseForgotPassword}
          sx={{
            "& .MuiDialog-paper": {
              backgroundColor: "#0E1720",
              color: "#A8B2D1",
              borderRadius: "3px",
              width: "400px",
              boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.8)",
            },
          }}
          BackdropProps={{
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.85)",
            },
          }}
        >
          <DialogTitle sx={{ color: "#FFFFFF", fontWeight: "bold", textAlign: "center" }}>
            Reset Password
          </DialogTitle>

          <DialogContent>
            <TextField
              label="Enter your email"
              fullWidth
              margin="normal"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              InputLabelProps={{ style: { color: "#868B8F" } }}
              InputProps={{
                style: { color: "#FFFFFF" },
              }}
              sx={{
                "& .MuiInputLabel-asterisk": { display: "none" },
                "& .MuiInputLabel-root": { color: "#868B8F" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
                "& .MuiInputBase-input": { color: "#FFFFFF" },
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { borderColor: "#868B8F" },
                  "&:hover fieldset": { borderColor: "#868B8F" },
                  "&.Mui-focused fieldset": { borderColor: "#ffffff" },
                },
              }}
            />
            {forgotPasswordMessage && (
              <Typography sx={{ color: "#ffffff", textAlign: "center", marginTop: 1 }}>
                {forgotPasswordMessage}
              </Typography>
            )}
            {error && (
              <Typography sx={{ color: "#FF6B6B", textAlign: "center", marginTop: 1 }}>
                {error}
              </Typography>
            )}
          </DialogContent>

          <DialogActions sx={{ justifyContent: "space-between", padding: "16px" }}>
            <Button
              onClick={handleCloseForgotPassword}
              sx={{
                color: "#B5B7BA",
                "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255, 255, 255, 0.1)", boxShadow: "0px" },
                fontWeight: "bold",
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleForgotPassword}
              variant="contained"
              sx={{
                backgroundColor: "#1DB0BF",
                color: "#FFFFFF",
                "&:hover": { backgroundColor: "#1D6F78" },
                boxShadow: "none",
                fontWeight: "bold",
              }}
            >
              Send Reset Link
            </Button>
          </DialogActions>
        </Dialog>

        <form onSubmit={handleSubmit}>
          <TextField
            id="identifier-input"
            label="Username or Email"
            variant="outlined"
            fullWidth
            margin="normal"
            value={identifier}
            onChange={(e) => setIdentifier(e.target.value)}
            required
            sx={{
              "& .MuiInputLabel-asterisk": { display: "none" },
              "& .MuiInputLabel-root": { color: "#A8B2D1" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
              "& .MuiInputBase-input": { color: "#FFFFFF" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#868B8F" },
                "&:hover fieldset": { borderColor: "#868B8F" },
                "&.Mui-focused fieldset": { borderColor: "#ffffff" },
              },
            }}
          />
          <TextField
            id="password-input"
            label="Password"
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            InputProps={{
              style: { color: "#FFFFFF" },
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={handleClickShowPassword}
                    edge="end"
                    sx={{ color: "#A8B2D1" }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-asterisk": { display: "none" },
              "& .MuiInputLabel-root": { color: "#A8B2D1" },
              "& .MuiInputLabel-root.Mui-focused": { color: "#FFFFFF" },
              "& .MuiInputBase-input": { color: "#FFFFFF" },
              "& .MuiOutlinedInput-root": {
                "& fieldset": { borderColor: "#868B8F" },
                "&:hover fieldset": { borderColor: "#868B8F" },
                "&.Mui-focused fieldset": { borderColor: "#ffffff" },
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{ textAlign: "right", cursor: "pointer", color: "grey" }}
            onClick={() => setOpenForgotPassword(true)}
          >
            Forgot Password?
          </Typography>
          {error && (
            <Typography
              id="error-message"
              variant="body2"
              sx={{
                color: "#FF6B6B",
                textAlign: "center",
                marginTop: 1,
                marginBottom: 2,
              }}
            >
              {error}
            </Typography>

          )}
          <Button
            id="login-button"
            type="submit"
            fullWidth
            variant="contained"
            sx={{
              marginTop: 2,
              backgroundColor: "#1DB0BF",
              color: "#FFFFFF",
              "&:hover": { backgroundColor: "#1D6F78" },
              display: "block",
              margin: "20px auto",
              width: "100%",
              height: "50px",
              textAlign: "center",
              fontWeight: "bold",
            }}
            disabled={loading}
          >
            {loading ? "Signing In..." : "Log in"}
          </Button>
        </form>
        <Box id="sign-up-link" sx={{ textAlign: "center", marginTop: 3 }}>
          <Typography
            variant="body2"
            sx={{ color: "#ffffff", marginBottom: 1 }}
          >
            <p className="mb-2">
              Not logged in?{" "}
              <Link to="/register" className="text-primary" style={{ color: "#1DB0BF", textDecoration: "none" }}>
                Sign up
              </Link>
            </p>
          </Typography>
        </Box>
      </Card>
    </Box>
  );
};

export default Login;
